import { Checkbox, Select } from "@mantine/core";
import * as _ from "lodash-es";
import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_ColumnDef
} from "mantine-react-table-open";
import { useCallback, useMemo } from "react";
import { getDefaultMRTOptions } from "../../../../mantine/getDefaultMRTOptions";
import { SiteLicense } from "../../../../types/api.types";
import type { SiteCategoriesWithSiteFeatures } from "../../../../utils/backend-types";
import "./SiteCategoriesTable.scss";

export type SiteCategoriesTableProps = {
  data: Array<SiteCategoriesWithSiteFeatures>;
  onChange: (data: Array<SiteCategoriesWithSiteFeatures>) => void;
  onChangeInvoiceStatus: (id: number, active: boolean) => void;
  onChangeMeteringConceptStatus: (id: number, active: boolean) => void;
  onChangeGoStatus: (id: number, active: boolean) => void;
  onChangeLicense: (id: number, newLicense: SiteLicense) => void;
  onChangePremiumTodosStatus: (id: number, active: boolean) => void;
};

function SiteCategoriesTable({
  data,
  onChange,
  onChangeInvoiceStatus,
  onChangeMeteringConceptStatus,
  onChangeGoStatus,
  onChangeLicense,
  onChangePremiumTodosStatus
}: SiteCategoriesTableProps) {
  const onCheckedChange = useCallback(
    (value: boolean, index: number, key: string) => {
      const newData = data.map((item, i) => {
        if (i === index) {
          return {
            ...item,
            [key]: value
          };
        }
        return item;
      });

      if (key === "is_partial_feedin_site" && !value) {
        newData[index] = {
          ...newData[index],
          is_partial_feedin_site: false
        };
      }

      onChange(newData);
    },
    [data, onChange]
  );

  const columns = useMemo<Array<MRT_ColumnDef<SiteCategoriesWithSiteFeatures>>>(
    () => [
      {
        accessorKey: "name",
        header: "Liegenschaft"
      },
      {
        accessorKey: "is_partial_feedin_site",
        header: "Teileinspeisung",
        Cell: ({ row, cell }) => (
          <div className="centered-checkbox-in-table">
            <Checkbox
              aria-label="Teileinspeisung"
              defaultChecked={cell.getValue<boolean>()}
              onChange={(event) =>
                onCheckedChange(
                  event.currentTarget.checked,
                  row.index,
                  "is_partial_feedin_site"
                )
              }
            />
          </div>
        )
      },
      {
        accessorKey: "is_eigenerzeugung_plus",
        header: "EEplus",
        Cell: ({ row, cell }) => (
          <div className="centered-checkbox-in-table">
            <Checkbox
              aria-label="EEplus"
              defaultChecked={cell.getValue<boolean>()}
              disabled={!row.original.is_partial_feedin_site}
              onChange={(event) =>
                onCheckedChange(
                  event.currentTarget.checked,
                  row.index,
                  "is_eigenerzeugung_plus"
                )
              }
            />
          </div>
        )
      },
      {
        accessorKey: "license",
        header: "Lizenz (Mieterstrom)",
        size: 160,
        Cell: ({ row }) => {
          return (
            <Select
              aria-label="Lizenz Mieterstrom"
              data={[
                { value: SiteLicense.standard, label: "Basis" },
                { value: SiteLicense.premium, label: "Premium" },
                { value: SiteLicense.light, label: "Light" }
              ]}
              defaultValue={row.original.license}
              placeholder="Lizenz"
              onChange={(newLicense) =>
                onChangeLicense(row.original.id, newLicense as SiteLicense)
              }
            />
          );
        }
      },
      {
        accessorKey: "invoicing_active",
        header: "Rechnungsstellung aktiv",
        Cell: ({ row, cell }) => (
          <div className="centered-checkbox-in-table">
            <Checkbox
              aria-label="Rechnungsstellung aktiv"
              defaultChecked={cell.getValue<boolean>()}
              onChange={(event) =>
                onChangeInvoiceStatus(
                  row.original.id,
                  event.currentTarget.checked
                )
              }
            />
          </div>
        )
      },
      {
        accessorKey: "metering_concept_v2_active",
        header: "MBK V2 für Rechnungserstellung",
        Cell: ({ row, cell }) => (
          <div className="centered-checkbox-in-table">
            <Checkbox
              aria-label="MBK V2 für Rechnungserstellung"
              defaultChecked={cell.getValue<boolean>()}
              disabled={!row.original.invoicing_active}
              onChange={(event) =>
                onChangeMeteringConceptStatus(
                  row.original.id,
                  event.currentTarget.checked
                )
              }
            />
          </div>
        )
      },
      {
        accessorKey: "go_active",
        header: "Dokumentation HKN",
        Cell: ({ row, cell }) => (
          <div className="centered-checkbox-in-table">
            <Checkbox
              aria-label="Dokumentation HKN"
              defaultChecked={cell.getValue<boolean>()}
              disabled={
                !row.original.is_partial_feedin_site ||
                row.original.is_eigenerzeugung_plus
              }
              onChange={(event) =>
                onChangeGoStatus(row.original.id, event.currentTarget.checked)
              }
            />
          </div>
        )
      },
      {
        accessorKey: "is_ppaaas",
        header: "PPA-as-a-Service",
        Cell: ({ row, cell }) => (
          <div className="centered-checkbox-in-table">
            <Checkbox
              aria-label="PPA-as-a-Service"
              defaultChecked={cell.getValue<boolean>()}
              onChange={(event) =>
                onCheckedChange(
                  event.currentTarget.checked,
                  row.index,
                  "is_ppaaas"
                )
              }
            />
          </div>
        )
      },
      {
        accessorKey: "premium_todos_active",
        header: "Premium-Aufgaben",
        Cell: ({ row, cell }) => (
          <div className="centered-checkbox-in-table">
            <Checkbox
              aria-label="Premium-Aufgaben"
              defaultChecked={cell.getValue<boolean>()}
              disabled={row.original.license !== SiteLicense.premium}
              onChange={(event) =>
                onChangePremiumTodosStatus(
                  row.original.id,
                  event.currentTarget.checked
                )
              }
            />
          </div>
        )
      }
    ],
    [
      onCheckedChange,
      onChangeInvoiceStatus,
      onChangeMeteringConceptStatus,
      onChangeGoStatus,
      onChangeLicense,
      onChangePremiumTodosStatus
    ]
  );

  const defaultOptions = getDefaultMRTOptions<SiteCategoriesWithSiteFeatures>({
    emptyRowsFallbackText: "Keine Liegenschaftsinformationen verfügbar",
    initialState: {
      sorting: [{ id: "name", desc: true }]
    },
    hideBorder: true
  });

  const tableOptions = _.merge({}, defaultOptions, {
    enableColumnActions: false,
    enableFilters: false,
    enableSorting: false
  });

  const table = useMantineReactTable({
    ...tableOptions,
    columns,
    data
  });

  return (
    <div className="SiteCategoriesTable">
      <MantineReactTable table={table} />
    </div>
  );
}

export { SiteCategoriesTable };
